import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { api } from '../../context/AuthContext';
import { ShieldAlert, Search, RefreshCw, AlertTriangle, CheckCircle, AlertCircle, Info } from 'lucide-react';

const RISK_CONFIG = {
  low: { label: 'Low Risk', color: 'text-green-600 dark:text-green-400', bg: 'bg-green-50 dark:bg-green-950/30', bar: 'bg-green-500', icon: CheckCircle },
  moderate: { label: 'Moderate Risk', color: 'text-amber-600 dark:text-amber-400', bg: 'bg-amber-50 dark:bg-amber-950/30', bar: 'bg-amber-500', icon: Info },
  elevated: { label: 'Elevated Risk', color: 'text-orange-600 dark:text-orange-400', bg: 'bg-orange-50 dark:bg-orange-950/30', bar: 'bg-orange-500', icon: AlertCircle },
  high: { label: 'High Risk', color: 'text-red-600 dark:text-red-400', bg: 'bg-red-50 dark:bg-red-950/30', bar: 'bg-red-500', icon: AlertTriangle },
};

export default function PenaltyCheckPage() {
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [history, setHistory] = useState([]);

  const loadHistory = async () => {
    try {
      const { data } = await api.get('/penalty-check/history');
      setHistory(data.history || []);
    } catch {
      // silent
    }
  };

  useEffect(() => {
    loadHistory();
  }, []);

  const handleCheck = async (e) => {
    e.preventDefault();
    if (!url.trim()) return;
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const { data } = await api.post('/penalty-check/check', { url: url.trim() });
      if (!data.success) {
        setError(data.error || 'Check failed. Ensure the URL is accessible.');
      } else {
        setResult(data);
        await loadHistory();
      }
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === 'string' ? d : err.message || 'Request failed');
    } finally {
      setLoading(false);
    }
  };

  const riskLevel = result?.risk_level || 'low';
  const riskCfg = RISK_CONFIG[riskLevel] || RISK_CONFIG.low;
  const RiskIcon = riskCfg.icon;

  return (
    <motion.div
      className="space-y-6 p-4 md:p-6"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
    >
      <div className="flex items-start gap-3">
        <div className="p-2 rounded-lg bg-rose-100 dark:bg-rose-900/30">
          <ShieldAlert className="w-5 h-5 text-rose-600 dark:text-rose-400" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Penalty Check</h1>
          <p className="text-slate-500 dark:text-slate-400 mt-0.5 text-sm">
            Analyse a URL for algorithmic-demotion risk signals based on Google's Spam Policies and Helpful Content guidelines.
          </p>
        </div>
      </div>

      {/* URL input */}
      <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-6 shadow-sm">
        <form onSubmit={handleCheck} className="flex gap-3 max-w-2xl">
          <input
            type="text"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://yoursite.com/page-to-check"
            className="flex-1 p-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
            required
          />
          <button
            type="submit"
            disabled={loading}
            className="flex items-center gap-2 bg-rose-600 text-white px-5 py-2 rounded-md text-sm hover:bg-rose-700 disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
          >
            <Search className="w-4 h-4" />
            {loading ? 'Analysing...' : 'Run Check'}
          </button>
        </form>

        <div className="mt-3 p-3 rounded-lg bg-slate-50 dark:bg-slate-900 text-xs text-slate-500 dark:text-slate-400 flex gap-2">
          <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
          <span>
            Manual actions can only be confirmed in Google Search Console. This tool analyses observable on-page signals for algorithmic risk.
          </span>
        </div>

        {error && (
          <div className="mt-4 p-3 rounded-md bg-red-50 dark:bg-red-950/40 text-red-700 dark:text-red-300 text-sm flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            {error}
          </div>
        )}
      </div>

      {/* Results */}
      {result && (
        <motion.div
          className="space-y-5"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Score card */}
          <div className={`rounded-xl border p-6 shadow-sm ${riskCfg.bg} border-slate-200 dark:border-slate-800`}>
            <div className="flex items-center justify-between gap-4 mb-4">
              <div className="flex items-center gap-3">
                <RiskIcon className={`w-6 h-6 ${riskCfg.color}`} />
                <div>
                  <p className="text-sm font-medium text-slate-700 dark:text-slate-300">Algorithmic Risk Score</p>
                  <p className={`text-3xl font-bold ${riskCfg.color}`}>{result.algorithmic_risk_score ?? '—'}<span className="text-base font-normal">/100</span></p>
                </div>
              </div>
              <span className={`text-sm font-semibold px-3 py-1 rounded-full ${riskCfg.color} ${riskCfg.bg}`}>
                {riskCfg.label}
              </span>
            </div>

            {result.algorithmic_risk_score !== undefined && (
              <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2">
                <div
                  className={`h-2 rounded-full ${riskCfg.bar} transition-all duration-500`}
                  style={{ width: `${result.algorithmic_risk_score}%` }}
                />
              </div>
            )}

            {result.analysis && (
              <p className="mt-3 text-sm text-slate-600 dark:text-slate-400">{result.analysis}</p>
            )}
          </div>

          {/* Manual actions note */}
          {result.manual_actions && (
            <div className={`rounded-lg border p-4 text-sm flex gap-2 ${
              result.manual_actions.available
                ? 'border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-950/20 text-green-700 dark:text-green-300'
                : 'border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/20 text-amber-700 dark:text-amber-300'
            }`}>
              <Info className="w-4 h-4 shrink-0 mt-0.5" />
              {result.manual_actions.note}
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-5">
            {/* Findings */}
            {result.findings?.length > 0 && (
              <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-5 shadow-sm">
                <h2 className="text-sm font-semibold text-slate-900 dark:text-white mb-3">Findings</h2>
                <div className="space-y-3">
                  {result.findings.map((f, i) => (
                    <div key={i} className="text-xs space-y-1 pb-3 border-b border-slate-100 dark:border-slate-800 last:border-0 last:pb-0">
                      <p className="font-medium text-slate-800 dark:text-slate-200">{f.signal}</p>
                      <p className="text-slate-600 dark:text-slate-400">{f.observation}</p>
                      {f.concern && <p className="text-amber-600 dark:text-amber-400">{f.concern}</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Recommendations */}
            {result.recommendations?.length > 0 && (
              <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-5 shadow-sm">
                <h2 className="text-sm font-semibold text-slate-900 dark:text-white mb-3">Recommendations</h2>
                <ul className="space-y-2">
                  {result.recommendations.map((rec, i) => (
                    <li key={i} className="flex items-start gap-2 text-xs text-slate-600 dark:text-slate-400">
                      <span className="w-4 h-4 rounded-full bg-rose-100 dark:bg-rose-900/40 text-rose-600 dark:text-rose-400 flex items-center justify-center shrink-0 font-bold">{i + 1}</span>
                      {rec}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Likely triggers */}
          {result.likely_triggers?.length > 0 && (
            <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-5 shadow-sm">
              <h2 className="text-sm font-semibold text-slate-900 dark:text-white mb-3">Likely Risk Triggers</h2>
              <div className="flex flex-wrap gap-2">
                {result.likely_triggers.map((t, i) => (
                  <span key={i} className="text-xs bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-300 px-3 py-1 rounded-full capitalize">
                    {t}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Signals */}
          {result.signals && (
            <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-5 shadow-sm">
              <h2 className="text-sm font-semibold text-slate-900 dark:text-white mb-3">Raw Signals</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {[
                  { key: 'word_count', label: 'Word Count' },
                  { key: 'external_link_count', label: 'External Links' },
                  { key: 'internal_link_count', label: 'Internal Links' },
                  { key: 'title_length', label: 'Title Length' },
                  { key: 'meta_description_length', label: 'Meta Desc. Length' },
                  { key: 'status_code', label: 'HTTP Status' },
                ].map(({ key, label }) => (
                  result.signals[key] !== undefined && (
                    <div key={key} className="rounded-lg bg-slate-50 dark:bg-slate-900 p-3">
                      <p className="text-base font-bold text-slate-800 dark:text-slate-200">{result.signals[key]}</p>
                      <p className="text-xs text-slate-500 mt-0.5">{label}</p>
                    </div>
                  )
                ))}
              </div>
              {result.signals.meta_robots && (
                <p className="text-xs text-slate-500 mt-2">meta robots: <code className="text-slate-700 dark:text-slate-300">{result.signals.meta_robots}</code></p>
              )}
            </div>
          )}
        </motion.div>
      )}

      {/* History placeholder */}
      {!result && history.length > 0 && (
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-5 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-slate-900 dark:text-white">Check History</h2>
            <button
              type="button"
              onClick={loadHistory}
              className="flex items-center gap-1 text-xs text-rose-600 hover:text-rose-700"
            >
              <RefreshCw className="w-3 h-3" /> Refresh
            </button>
          </div>
          <div className="space-y-2">
            {history.map((h, i) => (
              <div key={i} className="flex items-center justify-between text-sm py-2 border-b border-slate-100 dark:border-slate-800 last:border-0">
                <span className="text-slate-600 dark:text-slate-400 truncate">{h.url}</span>
                {h.risk_level && (
                  <span className={`text-xs px-2 py-0.5 rounded-full capitalize ${RISK_CONFIG[h.risk_level]?.color || ''} ${RISK_CONFIG[h.risk_level]?.bg || ''}`}>
                    {h.risk_level}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </motion.div>
  );
}
